/**
 * Lightweight A/B test bucket assignment.
 *
 * Sticky per browser via localStorage so the same user always sees the same
 * variant (consistent UX + clean conversion attribution).
 * Tracking fires once per session per test so GA captures cohort assignment.
 */
import { trackEvent } from './tracking';

const SESSION_FIRED_KEY = '_ab_fired_v1';

/**
 * Get a sticky variant bucket for a named experiment.
 *
 * @param {string} testName        e.g. "booking_lang"
 * @param {string[]} variants      e.g. ["bg", "en-us"]
 * @param {string} defaultVariant  fallback if storage is unavailable
 * @returns {string}               the assigned variant
 */
export function getVariant(testName, variants, defaultVariant) {
  const fallback = defaultVariant ?? variants[0];

  if (typeof window === 'undefined' || !window.localStorage) return fallback;

  const storageKey = `ab_${testName}`;

  let assigned = null;
  try {
    assigned = window.localStorage.getItem(storageKey);
  } catch {
    return fallback;
  }

  if (!assigned || !variants.includes(assigned)) {
    // Fresh assignment — random pick
    assigned = variants[Math.floor(Math.random() * variants.length)];
    try {
      window.localStorage.setItem(storageKey, assigned);
    } catch {
      /* private mode / quota exceeded — keep the in-memory pick */
    }
  }

  // Fire cohort event ONCE per session per test so we don't spam analytics
  try {
    const fired = JSON.parse(window.sessionStorage?.getItem(SESSION_FIRED_KEY) || '{}');
    if (!fired[testName]) {
      trackEvent('ab_assign', { test: testName, variant: assigned });
      fired[testName] = true;
      window.sessionStorage?.setItem(SESSION_FIRED_KEY, JSON.stringify(fired));
    }
  } catch {
    /* sessionStorage unavailable — skip dedupe */
  }

  return assigned;
}

/**
 * Decide which `lang=` parameter to send to Booking.com for a given user.
 *
 * Hypothesis: Bulgarian users may convert better on Booking.com when the
 * results page is in Bulgarian (familiarity, trust). But Booking.com's BG
 * translation is mediocre in places, so EN-US could also win on perceived
 * professionalism. 50/50 split for BG users; EN-US for everyone else.
 */
export function getBookingLangVariant(userLanguage) {
  if (userLanguage !== 'bg') return 'en-us';
  return getVariant('booking_lang', ['bg', 'en-us'], 'bg');
}
